import { PropertyPhoto } from '@/integrations/mysql/types';
import {
  apiFormUpload,
  apiJson,
  apiJsonArray,
  ApiRequestError,
  normalizeApiArray,
} from '@/lib/api-client';

export interface PropertyUploadResult {
  success?: boolean;
  filename: string;
  path?: string;
  propertyId?: number;
}

export class PropertyPhotoService {
  static async uploadFile(
    propertyId: number,
    file: File,
  ): Promise<PropertyUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('propertyId', propertyId.toString());
    const result = await apiFormUpload<PropertyUploadResult>(
      'api/upload',
      formData,
    );
    if (!result?.filename) {
      throw new Error('Upload concluído sem nome de arquivo. Tente novamente.');
    }
    return result;
  }

  static async uploadPhoto(
    propertyId: number,
    file: File,
    caption?: string,
  ): Promise<PropertyPhoto> {
    const { filename } = await this.uploadFile(propertyId, file);
    return apiJson<PropertyPhoto>('api/property-photos', {
      method: 'POST',
      body: JSON.stringify({
        property_id: propertyId,
        filename,
        caption: caption || '',
        display_order: 0,
      }),
    });
  }

  static async getPhotosByPropertyId(propertyId: number): Promise<PropertyPhoto[]> {
    return apiJsonArray<PropertyPhoto>(
      `api/property-photos/property/${propertyId}`,
    );
  }

  static async getActivePhotosByPropertyId(
    propertyId: number,
  ): Promise<PropertyPhoto[]> {
    return apiJsonArray<PropertyPhoto>(
      `api/property-photos/property/${propertyId}/active`,
    );
  }

  static async getFirstPhotoByPropertyId(
    propertyId: number,
  ): Promise<PropertyPhoto | null> {
    return apiJson<PropertyPhoto | null>(
      `api/property-photos/property/${propertyId}/first`,
    );
  }

  static async addPhoto(photoData: {
    property_id: number;
    filename: string;
    caption?: string;
    display_order?: number;
  }): Promise<PropertyPhoto> {
    return apiJson<PropertyPhoto>('api/property-photos', {
      method: 'POST',
      body: JSON.stringify({
        ...photoData,
        caption: photoData.caption || '',
        display_order: photoData.display_order || 0,
      }),
    });
  }

  static async updatePhoto(
    photoId: number,
    updateData: {
      filename?: string;
      caption?: string;
      display_order?: number;
      is_active?: boolean;
    },
  ): Promise<PropertyPhoto> {
    return apiJson<PropertyPhoto>(`api/property-photos/${photoId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  static async deletePhoto(photoId: number): Promise<{ message: string }> {
    await apiJson(`api/property-photos/${photoId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: false }),
    });
    return { message: 'Foto deletada com sucesso' };
  }

  static async deactivatePhoto(photoId: number): Promise<PropertyPhoto> {
    return this.updatePhoto(photoId, { is_active: false });
  }

  static async reorderPhotos(
    propertyId: number,
    photoOrders: { id: number; display_order: number }[],
  ): Promise<{ message: string }> {
    return apiJson<{ message: string }>(
      `api/property-photos/property/${propertyId}/reorder`,
      {
        method: 'PUT',
        body: JSON.stringify({ photo_orders: photoOrders }),
      },
    );
  }

  static async addMultiplePhotos(
    propertyId: number,
    photos: { filename: string; caption?: string }[],
  ): Promise<PropertyPhoto[]> {
    return apiJsonArray<PropertyPhoto>(
      `api/property-photos/property/${propertyId}/multiple`,
      {
        method: 'POST',
        body: JSON.stringify({ photos }),
      },
    );
  }

  static async getPhotoById(photoId: number): Promise<PropertyPhoto> {
    return apiJson<PropertyPhoto>(`api/property-photos/${photoId}`);
  }

  static async updatePhotoCaption(
    photoId: number,
    caption: string,
  ): Promise<PropertyPhoto> {
    return this.updatePhoto(photoId, { caption });
  }

  static async getPhotosWithProperty(propertyId: number): Promise<{
    photos: PropertyPhoto[];
    property: { id: number; title: string; city: string };
  }> {
    const data = await apiJson<{
      photos: PropertyPhoto[];
      property: { id: number; title: string; city: string };
    }>(`api/property-photos/property/${propertyId}/with-property`);
    return {
      photos: normalizeApiArray<PropertyPhoto>(data.photos),
      property: {
        id: data.property.id,
        title: data.property.title,
        city: data.property.city,
      },
    };
  }

  static formatUploadError(error: unknown): string {
    if (error instanceof ApiRequestError) {
      if (error.status === 401) {
        return 'Sessão expirada. Faça login novamente e tente enviar as fotos.';
      }
      return error.message;
    }
    if (error instanceof Error) return error.message;
    return 'Erro ao enviar foto. Tente novamente.';
  }
}
